import {
  AGENTIC_LOGIC_CREATE_RULE_TOOL,
  AGENTIC_LOGIC_RULE_PROPOSAL_TOOL,
  AGENTIC_LOGIC_TEMPLATE_RULE_PROPOSAL_TOOL,
  AGENTIC_LOGIC_VALIDATE_TEMPLATE_TOOL,
} from "@intelligems/ig-types/dtos/agentic-logic/rule-proposal";
import { type AiChatTurn } from "@intelligems/ig-types/schemas/ai/ai-chat-state";

import type { AgenticLogicView } from "@src/features/ai/pages/agentic-logic/surface/ruleViewParams";

/**
 * Where a new-rule conversation stands.
 *
 * - `clarifying` — Atlas is still asking what the rule should do. There is
 *   nothing to sketch yet, so the conversation takes the whole work area.
 * - `building` — Atlas has enough to go on and is constructing the rule; the
 *   creation view shows the skeleton workspace the rule is about to fill.
 *
 * Once the rule exists the surface is on the rule's own view, which has no
 * phase: Atlas is docked there for the rest of the conversation.
 */
export type AtlasCreationPhase = "clarifying" | "building";

/**
 * The tools Atlas only reaches for once it has enough information to build the
 * rule: composing the approval card for a code rule or a template rule,
 * validating a template's parameters, or creating the rule row itself. The
 * first call to any of them is the clarifying → building hand-off.
 */
const RULE_BUILD_TOOLS: ReadonlySet<string> = new Set([
  AGENTIC_LOGIC_RULE_PROPOSAL_TOOL,
  AGENTIC_LOGIC_TEMPLATE_RULE_PROPOSAL_TOOL,
  AGENTIC_LOGIC_VALIDATE_TEMPLATE_TOOL,
  AGENTIC_LOGIC_CREATE_RULE_TOOL,
]);

/**
 * Whether the transcript shows Atlas has started building the rule.
 *
 * Read off the transcript rather than kept as its own flag, so the phase can
 * never disagree with the conversation on screen: it holds across a reload
 * that restores the transcript, it resets with the transcript on "New chat",
 * and it never moves back, because a tool call is never removed from a turn.
 * A part counts from the moment the call starts streaming — the tool name is
 * known before its input is.
 */
export const hasAtlasRuleBuildStarted = (
  turns: readonly AiChatTurn[]
): boolean =>
  turns.some((turn) =>
    turn.nodes.some((node) =>
      node.messages.some((message) =>
        message.parts.some(
          (part) =>
            part.type === "dynamic-tool" && RULE_BUILD_TOOLS.has(part.toolName)
        )
      )
    )
  );

/**
 * How the Atlas conversation is laid out.
 *
 * `full` is the conversation alone in one centred column with no canvas and no
 * rail; `docked` is the right-hand rail beside the page. They are the SAME
 * mounted chat at two widths — only the frame around it changes.
 *
 * Full screen belongs to the clarifying phase of a new rule on a standalone
 * surface and nothing else. It is never used on an existing rule, including
 * when Atlas asks it further questions. An embedded surface keeps its rail:
 * its host is a dialog whose own layout frames the builder.
 */
export type AtlasChatLayout = "full" | "docked";

/** The narrower rail belongs to the conversation that created this rule. */
export const usesAtlasCreationRail = ({
  mode,
  layout,
  initialContext,
  createdRuleId,
}: {
  mode: string | null | undefined;
  layout: AtlasChatLayout;
  initialContext: unknown;
  createdRuleId: string | null;
}): boolean =>
  mode === "agentic-logic-creation" &&
  (layout === "full" || initialContext != null || createdRuleId !== null);

export const resolveAtlasChatLayout = ({
  surfaceKind,
  view,
  phase,
}: {
  surfaceKind: "standalone" | "embedded" | null;
  view: AgenticLogicView;
  phase: AtlasCreationPhase | null;
}): AtlasChatLayout =>
  surfaceKind === "standalone" && view.kind === "new" && phase === "clarifying"
    ? "full"
    : "docked";
